import React, { useState } from "react";
import QRCode from "react-qr-code";
import { toast } from "sonner";
import { Copy, Share2, Download, Printer, BadgeCheck, Sparkles } from "lucide-react";

const THEMES = {
  midnight: {
    label: "Midnight",
    swatch: "#1A1A1A",
    accent: "#D4AF37",
    css: "radial-gradient(120% 120% at 100% 0%, rgba(212,175,55,0.18) 0%, transparent 45%), radial-gradient(100% 100% at 0% 100%, rgba(139,30,63,0.35) 0%, transparent 50%), #1A1A1A",
    canvas: { base: "#1A1A1A", glow1: "rgba(212,175,55,0.22)", glow2: "rgba(139,30,63,0.45)", frame: "#D4AF37", accent: "#D4AF37" },
  },
  gold: {
    label: "Gold",
    swatch: "#C9A227",
    accent: "#F0C75E",
    css: "radial-gradient(120% 120% at 100% 0%, rgba(240,199,94,0.30) 0%, transparent 48%), radial-gradient(100% 100% at 0% 100%, rgba(120,80,10,0.55) 0%, transparent 55%), #1C1606",
    canvas: { base: "#1C1606", glow1: "rgba(240,199,94,0.30)", glow2: "rgba(120,80,10,0.55)", frame: "#F0C75E", accent: "#F0C75E" },
  },
  rose: {
    label: "Rose",
    swatch: "#C75D7E",
    accent: "#F0A6C0",
    css: "radial-gradient(120% 120% at 100% 0%, rgba(240,166,192,0.24) 0%, transparent 46%), radial-gradient(100% 100% at 0% 100%, rgba(139,30,63,0.6) 0%, transparent 58%), #3B0A1E",
    canvas: { base: "#3B0A1E", glow1: "rgba(240,166,192,0.26)", glow2: "rgba(139,30,63,0.6)", frame: "#F0A6C0", accent: "#F0A6C0" },
  },
};

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

async function generateBadge({ name, slug, verified, theme, eventName }) {
  const t = THEMES[theme].canvas;
  const W = 760, H = 1140;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  try { await document.fonts.ready; } catch {}

  ctx.fillStyle = t.base; ctx.fillRect(0, 0, W, H);
  const g1 = ctx.createRadialGradient(W, 0, 0, W, 0, W);
  g1.addColorStop(0, t.glow1); g1.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = g1; ctx.fillRect(0, 0, W, H);
  const g2 = ctx.createRadialGradient(0, H, 0, 0, H, W);
  g2.addColorStop(0, t.glow2); g2.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = g2; ctx.fillRect(0, 0, W, H);

  ctx.strokeStyle = t.frame; ctx.lineWidth = 3;
  roundRect(ctx, 36, 36, W - 72, H - 72, 28); ctx.stroke();

  ctx.textAlign = "center";
  ctx.fillStyle = t.accent;
  ctx.font = "600 26px 'JetBrains Mono', monospace";
  ctx.fillText("Q R C U P I D", W / 2, 116);
  ctx.fillStyle = "rgba(255,255,255,0.45)";
  ctx.font = "400 18px 'DM Sans', sans-serif";
  ctx.fillText("· VIP CONNECTION CODE ·", W / 2, 148);

  let nameY = 248;
  if (eventName && eventName.trim()) {
    // event ribbon
    const label = eventName.trim().toUpperCase();
    ctx.font = "600 30px 'DM Sans', sans-serif";
    const tw = Math.min(ctx.measureText(label).width + 60, W - 140);
    const rx = (W - tw) / 2;
    ctx.fillStyle = t.accent;
    roundRect(ctx, rx, 178, tw, 52, 26); ctx.fill();
    ctx.fillStyle = t.base;
    ctx.fillText(label, W / 2, 213);
    nameY = 300;
  }

  ctx.fillStyle = "#FFFFFF";
  ctx.font = "500 56px 'Playfair Display', serif";
  ctx.fillText(name + (verified ? "  ✓" : ""), W / 2, nameY);

  const plate = 430, px = (W - plate) / 2, py = nameY + 60;
  ctx.fillStyle = "#FFFFFF";
  roundRect(ctx, px, py, plate, plate, 24); ctx.fill();

  const svgEl = document.getElementById("qr-badge-svg");
  if (svgEl) {
    const xml = new XMLSerializer().serializeToString(svgEl);
    const svg64 = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(xml)));
    const img = new Image();
    await new Promise((res) => { img.onload = res; img.onerror = res; img.src = svg64; });
    const pad = 34;
    ctx.drawImage(img, px + pad, py + pad, plate - pad * 2, plate - pad * 2);
  }

  ctx.fillStyle = t.accent;
  ctx.font = "500 24px 'JetBrains Mono', monospace";
  ctx.fillText("/u/" + slug, W / 2, py + plate + 64);
  ctx.fillStyle = "rgba(255,255,255,0.7)";
  ctx.font = "400 26px 'DM Sans', sans-serif";
  ctx.fillText("Scan to view my profile", W / 2, py + plate + 118);

  return canvas.toDataURL("image/png");
}

export default function QrCard({ user }) {
  const url = `${window.location.origin}/u/${user.slug}`;
  const [busy, setBusy] = useState(false);
  const [theme, setTheme] = useState("midnight");
  const [eventName, setEventName] = useState("");
  const t = THEMES[theme];

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Profile link copied"); }
    catch { toast.error("Couldn't copy link"); }
  };
  const share = async () => {
    try { if (navigator.share) await navigator.share({ title: "My qrcupid", url }); else await copy(); }
    catch { /* cancelled */ }
  };

  const download = async () => {
    setBusy(true);
    try {
      const dataUrl = await generateBadge({ name: user.name, slug: user.slug, verified: user.verified_badge, theme, eventName });
      const a = document.createElement("a");
      a.href = dataUrl; a.download = `qrcupid-${user.slug}-${theme}-badge.png`; a.click();
      toast.success("Badge downloaded — print & wear it at your next event!");
    } catch (e) { console.error("badge download failed", e); toast.error("Could not generate badge."); }
    finally { setBusy(false); }
  };

  const printBadge = async () => {
    setBusy(true);
    try {
      const dataUrl = await generateBadge({ name: user.name, slug: user.slug, verified: user.verified_badge, theme, eventName });
      const w = window.open("", "_blank");
      w.document.write(`<html><head><title>qrcupid badge</title><style>
        @page{margin:0} body{margin:0;display:flex;align-items:center;justify-content:center;height:100vh;background:#fff}
        img{width:340px;max-width:90vw;border-radius:18px;box-shadow:0 10px 40px rgba(0,0,0,.2)}
      </style></head><body><img src="${dataUrl}" onload="setTimeout(()=>{window.print()},250)"/></body></html>`);
      w.document.close();
    } catch { toast.error("Could not open print view."); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-3xl p-7 text-white shadow-[0_24px_60px_rgba(28,25,23,0.25)] relative overflow-hidden transition-all duration-500" style={{ background: t.css }} data-testid="qr-card">
      <div className="flex items-center justify-between mb-5">
        <span className="font-mono text-xs tracking-[0.25em]" style={{ color: t.accent }}>VIP CODE</span>
        <Sparkles className="w-4 h-4" style={{ color: t.accent }} />
      </div>

      {eventName.trim() && (
        <div className="mb-3 text-center">
          <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider" style={{ background: t.accent, color: t.swatch }} data-testid="badge-event-pill">{eventName.trim()}</span>
        </div>
      )}

      <div className="bg-white rounded-2xl p-5 flex items-center justify-center" data-testid="qr-code">
        <QRCode id="qr-badge-svg" value={url} size={180} fgColor="#1A1A1A" bgColor="#FFFFFF" />
      </div>

      <div className="mt-5 text-center">
        <div className="font-display text-2xl font-medium flex items-center justify-center gap-1.5">
          {user.name}
          {user.verified_badge && <BadgeCheck className="w-5 h-5" style={{ color: t.accent }} fill={t.accent} stroke={t.swatch} />}
        </div>
        <div className="font-mono text-xs mt-1 break-all" style={{ color: t.accent }}>/u/{user.slug}</div>
      </div>

      {/* Customize */}
      <div className="mt-6 rounded-2xl bg-white/5 border border-white/10 p-4">
        <div className="text-[11px] uppercase tracking-[0.18em] text-white/50 mb-3">Customize your code</div>
        <div className="flex items-center gap-2.5 mb-3">
          {Object.entries(THEMES).map(([key, th]) => (
            <button
              key={key}
              onClick={() => setTheme(key)}
              data-testid={`badge-theme-${key}`}
              title={th.label}
              className={`w-8 h-8 rounded-full border-2 transition-all ${theme === key ? "scale-110" : "border-white/20 hover:border-white/50"}`}
              style={{ background: th.swatch, borderColor: theme === key ? th.accent : undefined }}
            />
          ))}
          <span className="text-sm text-white/70 ml-1">{t.label}</span>
        </div>
        <input
          value={eventName}
          onChange={(e) => setEventName(e.target.value.slice(0, 28))}
          data-testid="badge-event-input"
          placeholder="Event name (optional) — e.g. Friday Mixer"
          className="w-full bg-white/10 border border-white/15 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-white/40 transition-colors"
        />
      </div>

      <div className="grid grid-cols-2 gap-2.5 mt-5">
        <button onClick={download} disabled={busy} data-testid="qr-download-btn" className="flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-medium transition-all disabled:opacity-60" style={{ background: t.accent, color: t.swatch }}>
          <Download className="w-4 h-4" /> Download
        </button>
        <button onClick={printBadge} disabled={busy} data-testid="qr-print-btn" className="flex items-center justify-center gap-2 bg-white/10 border border-white/20 text-white rounded-full py-2.5 text-sm font-medium hover:bg-white/15 transition-all disabled:opacity-60">
          <Printer className="w-4 h-4" /> Print
        </button>
        <button onClick={copy} data-testid="qr-copy-btn" className="flex items-center justify-center gap-2 bg-white/5 border border-white/15 text-white/90 rounded-full py-2.5 text-sm font-medium hover:bg-white/10 transition-all">
          <Copy className="w-4 h-4" /> Copy Link
        </button>
        <button onClick={share} data-testid="qr-share-btn" className="flex items-center justify-center gap-2 bg-white/5 border border-white/15 text-white/90 rounded-full py-2.5 text-sm font-medium hover:bg-white/10 transition-all">
          <Share2 className="w-4 h-4" /> Share
        </button>
      </div>
      <p className="text-center text-[11px] text-white/40 mt-4">Pick a theme, add your event, then download or print your badge.</p>
    </div>
  );
}
